import React, {forwardRef, useEffect, useImperativeHandle} from 'react';
import {makeStyles} from 'tss-react/mui';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import CampoEditable from '../components/CampoEditable';
import PtrnAccionButton from '../components/PtrnAccionButton';
import PtrnTitulo from '../components/PtrnTitulo';
import usePtrnEstadoSimple from '../hooks/usePtrnEstadoSimple';
import {PtrnMainContentProps, PtrnStateRef} from '../patronProps';
import {
   ObjectStrKeys,
   PtrnAccion,
   PtrnAccionCancel,
   PtrnBotonPosicion,
   PtrnColors,
   PtrnIcons,
   PtrnState,
   PtrnTipoAccion
} from '../patronTypes';
import {
   PtrnReporteSimpleDefaultButtonProps,
   PtrnReporteSimpleDefaultButtonPropsFn,
   PtrnReporteSimpleProps
} from './PtrnReporteSimpleTypes';
import {PtrnEstilosGenerales} from '../patronUtils';
import PtrnContainerSimple from '../components/PtrnContainerSimple';
import {LocalizacionDefault} from '../../../common/localizacionApp';
import PtrnTituloGridHeader from '../components/PtrnTituloGridHeader';
import {defaultProps} from 'react-select/dist/declarations/src/Select';

const useStyles = makeStyles<number>()((theme, maxHeightVhMain) => {
   return {
      container: PtrnEstilosGenerales.containerSimpleMargin(theme),
      containerMainContent: PtrnEstilosGenerales.containerMainContent(maxHeightVhMain)
   };
});

function coalleceDefaultButtonProps(
   defaultProps:
      | PtrnReporteSimpleDefaultButtonProps
      | PtrnReporteSimpleDefaultButtonPropsFn
      | undefined,
   state: PtrnState
) {
   if (!defaultProps) {
      return {};
   }
   if (typeof defaultProps === 'function') {
      return defaultProps(state);
   }
   return defaultProps;
}

const PtrnReporteSimple = forwardRef<PtrnStateRef, PtrnReporteSimpleProps & PtrnMainContentProps>(
   (
      {
         camposConfig,
         titulo,
         children,
         onClickCancelar,
         cancelarButtonProps,
         onClickVistaPrevia,
         vistaPreviaButtonProps,
         onClickExcel,
         excelButtonProps,
         onClickTxt,
         pdfButtonProps,
         onClickPdf,
         txtButtonProps,
         maxHeightVhMain,
         distribucionForm,
         distribucionPreview,
         customButtons,
         localizacion
      },
      ref
   ) => {
      const {classes} = useStyles(maxHeightVhMain);
      const [state, handleOnChangeState, {handleOnBlur, inicializarEstado}] =
         usePtrnEstadoSimple(camposConfig);

      const existeChildren = Boolean(children);
      const bodyGridXs = existeChildren ? 6 : 12;
      //#region acciones footer
      const accionPdf: PtrnAccion = {
         label: LocalizacionDefault.acciones.pdf.label,
         icon: PtrnIcons.pdf,
         tipo: PtrnTipoAccion.positiva,
         fn: () => {
            if (onClickPdf) {
               onClickPdf({...state});
            }
         },
         descripcion: LocalizacionDefault.acciones.pdf.descripcion
      };

      const finalPdfButtonProps = coalleceDefaultButtonProps(pdfButtonProps, state);
      const finalCancelarButtonProps = coalleceDefaultButtonProps(cancelarButtonProps, state);
      const finalVistaPreviaButtonProps = coalleceDefaultButtonProps(vistaPreviaButtonProps, state);
      const finalExcelButtonProps = coalleceDefaultButtonProps(excelButtonProps, state);
      const finalTxtButtonProps = coalleceDefaultButtonProps(txtButtonProps, state);

      const accionTxt: PtrnAccion = {
         label: LocalizacionDefault.acciones.txt.label,
         icon: PtrnIcons.txt,
         tipo: PtrnTipoAccion.positiva,
         fn: () => {
            if (onClickTxt) {
               onClickTxt({...state});
            }
         },
         descripcion: LocalizacionDefault.acciones.txt.descripcion
      };
      const accionExcel: PtrnAccion = {
         label: LocalizacionDefault.acciones.excel.label,
         icon: PtrnIcons.excel,
         tipo: PtrnTipoAccion.positiva,
         fn: () => {
            if (onClickExcel) {
               onClickExcel({...state});
            }
         },
         descripcion: LocalizacionDefault.acciones.excel.descripcion
      };
      const accionVistaPrevia: PtrnAccion = {
         label: 'Vista Previa',
         icon: PtrnIcons.search,
         tipo: PtrnTipoAccion.positiva,
         fn: () => {
            if (onClickVistaPrevia) {
               onClickVistaPrevia({...state});
            }
         }
      };
      const accionCancel: PtrnAccionCancel = {
         label: 'Cancelar',
         icon: PtrnIcons.cancel,
         tipo: PtrnTipoAccion.neutra,
         fn: () => {
            if (onClickCancelar) {
               onClickCancelar({...state});
            }
            inicializarEstado();
         },
         descripcion: 'Reinicia los campos a sus valores por defecto'
      };
      //#endregion

      useImperativeHandle(ref, () => {
         return {
            state,
            handleOnChangeState
         };
      });

      const ptrnRef = null;
      const idCompPrefix = 'ptrn-rep-simple';
      //#region returns
      return (
         <PtrnContainerSimple id={`${idCompPrefix}-container`}>
            <Grid
               id={`${idCompPrefix}-grid-root`}
               container
               direction="column"
               justifyContent="space-between"
               spacing={2}
            >
               <PtrnTituloGridHeader idPrefix={idCompPrefix} titulo={titulo} />

               <Grid
                  id={`${idCompPrefix}-grid-body`}
                  container
                  alignItems="flex-start"
                  spacing={1}
                  item
                  xs={12}
               >
                  <Grid
                     id={`${idCompPrefix}-grid-body-form`}
                     className={classes.containerMainContent}
                     container
                     alignItems="center"
                     spacing={1}
                     item
                     xs={bodyGridXs}
                     {...distribucionForm}
                  >
                     {camposConfig.map((curr, inx) => {
                        return (
                           <CampoEditable
                              key={curr.name}
                              campoConfig={curr}
                              camposData={state}
                              value={
                                 state[curr.name] !== undefined ? state[curr.name].valor : state
                              }
                              onChange={handleOnChangeState}
                              onBlur={handleOnBlur}
                              ptrnRef={ptrnRef}
                           />
                        );
                     })}
                  </Grid>

                  {existeChildren && (
                     <Grid
                        id={`${idCompPrefix}-grid-body-preview`}
                        className={classes.containerMainContent}
                        container
                        alignItems="center"
                        spacing={1}
                        item
                        xs={bodyGridXs}
                        {...distribucionPreview}
                     >
                        {children}
                     </Grid>
                  )}
               </Grid>
               <Grid
                  id={`${idCompPrefix}-grid-footer`}
                  container
                  alignItems="center"
                  justifyContent="space-between"
                  item
                  spacing={1}
                  xs={12}
               >
                  {onClickCancelar && (
                     <PtrnAccionButton
                        name={`${idCompPrefix}-btn-cancelar`}
                        accion={accionCancel}
                        camposData={state}
                        {...finalCancelarButtonProps}
                        style={{
                           width: '180px',
                           ...finalCancelarButtonProps.style
                        }}
                     />
                  )}
                  {customButtons &&
                     customButtons.map((currButton) => {
                        const accionCustomButton: PtrnAccion = {
                           ...currButton.componentProps.accion,
                           fn: (data, auxParam) => {
                              if (currButton.onClick) {
                                 currButton.onClick({...state});
                              }
                              currButton.componentProps.accion.fn(data, auxParam);
                           }
                        };
                        return (
                           <PtrnAccionButton
                              key={currButton.componentProps.name}
                              {...currButton.componentProps}
                              accion={accionCustomButton}
                           />
                        );
                     })}
                  {onClickVistaPrevia && (
                     <PtrnAccionButton
                        name={`${idCompPrefix}-btn-preview`}
                        accion={accionVistaPrevia}
                        camposData={state}
                        posicionIcono={PtrnBotonPosicion.inicio}
                        {...finalVistaPreviaButtonProps}
                        style={{
                           width: '180px',
                           ...finalVistaPreviaButtonProps.style
                        }}
                     />
                  )}
                  {onClickExcel && (
                     <PtrnAccionButton
                        name={`${idCompPrefix}-btn-excel`}
                        accion={accionExcel}
                        camposData={state}
                        color={PtrnColors.success}
                        posicionIcono={PtrnBotonPosicion.inicio}
                        {...finalExcelButtonProps}
                        style={{
                           width: '180px',
                           ...finalExcelButtonProps.style
                        }}
                     />
                  )}
                  {onClickTxt && (
                     <PtrnAccionButton
                        name={`${idCompPrefix}-btn-txt`}
                        accion={accionTxt}
                        camposData={state}
                        posicionIcono={PtrnBotonPosicion.inicio}
                        color={PtrnColors.secondary}
                        {...finalTxtButtonProps}
                        style={{
                           width: '180px',
                           ...finalTxtButtonProps.style
                        }}
                     />
                  )}
                  {onClickPdf && (
                     <PtrnAccionButton
                        name={`${idCompPrefix}-btn-pdf`}
                        accion={accionPdf}
                        camposData={state}
                        posicionIcono={PtrnBotonPosicion.inicio}
                        color={PtrnColors.error}
                        {...finalPdfButtonProps}
                        style={{
                           width: '180px',
                           ...finalPdfButtonProps.style
                        }}
                     />
                  )}
               </Grid>
            </Grid>
         </PtrnContainerSimple>
      );
      //#endregion
   }
);

export default PtrnReporteSimple;
